import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../utils/constant';
import { FiLogOut, FiEye, FiSearch, FiXCircle, FiDownload, FiCalendar } from 'react-icons/fi';
import axios from 'axios';

// The useDebounce and Modal components remain the same...
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};


const Modal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 relative">
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition">
                        <FiXCircle size={24} />
                    </button>
                    {children}
                </div>
            </div>
        </div>
    );
};


const Profile = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [selectedForm, setSelectedForm] = useState(null);
    const [allForms, setAllForms] = useState([]);
    const [filters, setFilters] = useState({ search: '', work_status: '', payment_status: '' });
    // ✅ 1. ADDED state for date range filtering
    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const debouncedSearchTerm = useDebounce(filters.search, 300);
    const [downloadingFormId, setDownloadingFormId] = useState(null);

    // ✅ 2. ADDED effect to disable background scroll when modal is open
    useEffect(() => {
        const body = document.body;
        if (selectedForm) {
            body.style.overflow = 'hidden';
        } else {
            body.style.overflow = 'auto';
        }
        return () => { // Cleanup on component unmount
            body.style.overflow = 'auto';
        };
    }, [selectedForm]);


    useEffect(() => {
        const fetchAllForms = async () => {
            if (!user?.id) return;
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/application-form/by-user/${user.id}`);
                const data = await response.json();
                setAllForms(data.data || []);
            } catch (error) {
                console.error("Failed to fetch forms", error);
                setAllForms([]);
            } finally {
                setLoading(false);
            }
        };
        fetchAllForms();
    }, [user]);

    // ✅ 3. UPDATED filtering logic to include date range
    const filteredForms = useMemo(() => {
        return allForms.filter(form => {
            const searchMatch = debouncedSearchTerm ? form.businessName.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) : true;
            const workStatusMatch = filters.work_status ? form.work_status === filters.work_status : true;
            const paymentStatusMatch = filters.payment_status ? form.payment_status === filters.payment_status : true;

            const formDate = new Date(form.submissionDate);
            const startDate = dateRange.startDate ? new Date(dateRange.startDate + 'T00:00:00') : null;
            const endDate = dateRange.endDate ? new Date(dateRange.endDate + 'T23:59:59') : null;

            const dateMatch = (!startDate || formDate >= startDate) && (!endDate || formDate <= endDate);

            return searchMatch && workStatusMatch && paymentStatusMatch && dateMatch;
        });
    }, [allForms, debouncedSearchTerm, filters, dateRange]);


    const { paginatedForms, totalPages } = useMemo(() => {
        const total = Math.ceil(filteredForms.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginated = filteredForms.slice(startIndex, endIndex);
        return { paginatedForms: paginated, totalPages: total > 0 ? total : 1 };
    }, [filteredForms, currentPage, itemsPerPage]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    // ✅ 4. ADDED handler for date changes
    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };


    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    // ✅ 5. UPDATED reset logic to include dates
    const handleResetFilters = () => {
        setFilters({ search: '', work_status: '', payment_status: '' });
        setDateRange({ startDate: '', endDate: '' });
        setCurrentPage(1);
    };


    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const parseServices = (servicesString) => {
        if (!servicesString) return [];
        try {
            const services = JSON.parse(servicesString);
            return Array.isArray(services) ? services : [];
        } catch (error) {
            console.error("Failed to parse services:", error);
            return [];
        }
    };

    const downloadInvoice = async (formId) => {
        setDownloadingFormId(formId);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/application-forms/${formId}/download-estimate`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Estimate_Invoice_${formId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
            alert('Failed to download invoice.');
        } finally {
            setDownloadingFormId(null);
        }
    };

    if (!user) {
        return (
            <div className="text-center mt-32 mb-20">
                <p className="text-red-500">You must be logged in to view this page.</p>
                <button onClick={() => navigate('/login')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    Go to Login
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="max-w-7xl mx-auto mt-32 mb-20 px-4 sm:px-6 lg:px-8">
                <div className="w-full flex justify-end mb-4">
                    <Link
                        to={'/application'}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-sm font-semibold text-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mr-2"
                    >
                        Application Form
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </Link>
                </div>
                <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-8 border border-gray-200">
                    <div className="flex justify-between items-center flex-wrap gap-4 mb-8">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-800 overflow-hidden">👤 {user.name}</h2>
                            <p className="text-gray-600 text-sm">{user.email}</p>
                        </div>
                        <button onClick={logout} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md">
                            <FiLogOut /> Logout
                        </button>
                    </div>

                    <hr className="my-6" />

                    <h3 className="text-2xl font-semibold text-gray-800 mb-6 overflow-hidden">Submitted Forms</h3>

                    {/* ✅ 6. UPDATED Filter UI with date inputs */}
                    {/* ✅ Filter UI with Enhanced Date Picker UX */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg border">
                        {/* Search Input */}
                        <div className="relative lg:col-span-4">
                            <FiSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text" name="search" placeholder="Search by Business Name..."
                                value={filters.search} onChange={handleFilterChange}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Improved Date Range Picker */}
                        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-center bg-gray-50">
                            {/* Start Date */}
                            <div className="relative">
                                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                                    From
                                </label>
                                <input
                                    id="startDate"
                                    type="date"
                                    name="startDate"
                                    value={dateRange.startDate}
                                    onChange={handleDateChange}
                                    max={dateRange.endDate || ''} // Prevents start date from being after end date
                                    className="w-full p-2 border bg-white border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div className="text-center text-gray-500 mt-6 hidden sm:block">
                                -
                            </div>

                            {/* End Date */}
                            <div className="relative">
                                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                                    To
                                </label>
                                <input
                                    id="endDate"
                                    type="date"
                                    name="endDate"
                                    value={dateRange.endDate}
                                    onChange={handleDateChange}
                                    min={dateRange.startDate || ''} // Prevents end date from being before start date
                                    disabled={!dateRange.startDate} // Disabled until a start date is picked
                                    className="w-full p-2 border bg-white border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-200 disabled:cursor-not-allowed"
                                />
                            </div>
                        </div>

                        {/* Dropdown Filters */}
                        <select name="work_status" value={filters.work_status} onChange={handleFilterChange} className="w-full p-2 self-end border bg-white border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500">
                            <option value="">All Work Status</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                        </select>
                        <select name="payment_status" value={filters.payment_status} onChange={handleFilterChange} className="w-full p-2 self-end border bg-white border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500">
                            <option value="">All Payment Status</option>
                            <option value="Completely Paid">Completely Paid</option>
                            <option value="Not Paid">Not Paid</option>
                        </select>

                        {/* Reset Button */}
                        <div className="lg:col-span-4 text-right">
                            <button onClick={handleResetFilters} className="text-sm text-blue-600 hover:underline">Reset All Filters</button>
                        </div>
                    </div>

                    {/* ✅ 7. RESPONSIVE LAYOUT: Table for Desktop, Cards for Mobile */}

                    {/* --- TABLE for Medium screens and up --- */}
                    <div className="overflow-x-auto hidden md:block">
                        <table className="min-w-full bg-white border-collapse">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="text-left py-3 px-4 font-semibold text-gray-600 border-b">Business Name</th>
                                    <th className="text-left py-3 px-4 font-semibold text-gray-600 border-b">Submission Date</th>
                                    <th className="text-center py-3 px-4 font-semibold text-gray-600 border-b">Work Status</th>
                                    <th className="text-center py-3 px-4 font-semibold text-gray-600 border-b">Payment</th>
                                    <th className="text-center py-3 px-4 font-semibold text-gray-600 border-b">Invoice</th>
                                    <th className="text-center py-3 px-4 font-semibold text-gray-600 border-b">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="6" className="text-center py-10 text-gray-500">Loading forms...</td></tr>
                                ) : paginatedForms.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-10 text-gray-500">No forms found matching your criteria.</td></tr>
                                ) : (
                                    paginatedForms.map((form) => (
                                        <tr key={form.id || form.pan} className="hover:bg-gray-50">
                                            <td className="py-3 px-4 border-b border-gray-200">
                                                <div className="font-medium text-gray-800">{form.businessName}</div>
                                                <div className="text-sm text-gray-500">{form.ownerName}</div>
                                            </td>
                                            <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-600">{new Date(form.submissionDate).toLocaleDateString()}</td>
                                            <td className="py-3 px-4 border-b border-gray-200 text-center">
                                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${form.work_status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                                    {form.work_status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 border-b border-gray-200 text-center">
                                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${form.payment_status === 'Completely Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {form.payment_status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 border-b border-gray-200 text-center">
                                                <button
                                                    onClick={() => downloadInvoice(form.id)}
                                                    disabled={downloadingFormId === form.id}
                                                    className="inline-flex items-center justify-center gap-1.5 min-w-[120px] px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm font-semibold disabled:bg-blue-50 disabled:cursor-not-allowed"
                                                >
                                                    {downloadingFormId === form.id ? (
                                                        <span>Downloading...</span>
                                                    ) : (
                                                        <>
                                                            <FiDownload />
                                                            <span>Download</span>
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td className="py-3 px-4 border-b border-gray-200 text-center">
                                                <button onClick={() => setSelectedForm(form)} className="flex items-center gap-1.5 mx-auto px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm">
                                                    <FiEye /> Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* --- CARDS for Small screens --- */}
                    <div className="grid grid-cols-1 gap-4 md:hidden">
                        {loading ? (
                            <div className="text-center py-10 text-gray-500">Loading forms...</div>
                        ) : paginatedForms.length === 0 ? (
                            <div className="text-center py-10 text-gray-500">No forms found matching your criteria.</div>
                        ) : (
                            paginatedForms.map((form) => (
                                <div key={form.id || form.pan} className="bg-white border border-gray-200 rounded-lg p-4 shadow space-y-3">
                                    <div>
                                        <div className="font-bold text-lg text-gray-800">{form.businessName}</div>
                                        <div className="text-sm text-gray-500">{form.ownerName}</div>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        <strong>Submitted:</strong> {new Date(form.submissionDate).toLocaleDateString()}
                                    </div>
                                    <div className="flex flex-wrap gap-4 justify-between items-center text-sm">
                                        <div className="flex items-center gap-2">
                                            <strong>Work:</strong>
                                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold 
      ${form.work_status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                                {form.work_status}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <strong>Payment:</strong>
                                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold 
      ${form.payment_status === 'Completely Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {form.payment_status}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                                        <button
                                            onClick={() => downloadInvoice(form.id)}
                                            disabled={downloadingFormId === form.id}
                                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm font-semibold disabled:bg-blue-50 disabled:cursor-not-allowed"
                                        >
                                            {downloadingFormId === form.id ? 'Downloading...' : <><FiDownload /> Download</>}
                                        </button>
                                        <button onClick={() => setSelectedForm(form)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm">
                                            <FiEye /> Details
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {!loading && filteredForms.length > 0 && (
                        <div className="flex justify-between items-center flex-wrap gap-4 mt-6">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">Rows per page:</span>
                                <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="p-1 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500">
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                            <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                            <div className="flex gap-2">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Previous
                                </button>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Details Modal (No changes needed here) */}
            <Modal isOpen={!!selectedForm} onClose={() => setSelectedForm(null)}>
                {selectedForm && (
                    <div>
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-800 overflow-hidden">{selectedForm.businessName}</h3>
                                <p className="text-gray-500 text-sm">Submitted on: {new Date(selectedForm.submissionDate).toLocaleString()}</p>
                            </div>
                        </div>
                        <hr className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-gray-700 text-sm">
                            <p><strong>Owner:</strong> {selectedForm.ownerName}</p>
                            <p><strong>City:</strong> {selectedForm.city}</p>
                            <p><strong>State:</strong> {selectedForm.state}</p>
                            <p><strong>Business Type:</strong> {selectedForm.businessType}</p>
                            <p><strong>Company Type:</strong> {selectedForm.companyType}</p>
                            <p><strong>GSTIN:</strong> {selectedForm.gstin || 'N/A'}</p>
                            <p><strong>PAN:</strong> {selectedForm.pan || 'N/A'}</p>
                            <p><strong>BDM Code:</strong> {selectedForm.bdmCode || 'N/A'}</p>
                            <p className="flex items-center gap-2"><strong>Work Status:</strong> <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${selectedForm.work_status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{selectedForm.work_status}</span></p>
                            <p className="flex items-center gap-2"><strong>Payment Status:</strong> <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${selectedForm.payment_status === 'Completely Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{selectedForm.payment_status}</span></p>
                            <p><strong>Payment Type:</strong> {selectedForm.paymentType}</p>

                            {selectedForm.paymentType === 'Online' && (
                                <>
                                    <p><strong>Transaction ID:</strong> {selectedForm.transactionId}</p>
                                    <p><strong>Amount:</strong> ₹{selectedForm.total_amount ? Number(selectedForm.total_amount).toLocaleString('en-IN') : 'N/A'}</p>
                                </>
                            )}
                            {selectedForm.paymentType === 'Cheque' && (
                                <>
                                    <p><strong>Cheque No:</strong> {selectedForm.chequeNumber}</p>
                                    <p><strong>Cheque Date:</strong> {new Date(selectedForm.chequeDate).toLocaleDateString()}</p>
                                    <p><strong>Bank:</strong> {selectedForm.bankName}</p>
                                    <p><strong>Branch:</strong> {selectedForm.branch}</p>
                                    <p><strong>Amount:</strong> ₹{selectedForm.total_amount ? Number(selectedForm.total_amount).toLocaleString('en-IN') : 'N/A'}</p>
                                </>
                            )}
                            {selectedForm.paymentType === 'Others' && (
                                <>
                                    <p><strong>Amount:</strong> ₹{selectedForm.total_amount ? Number(selectedForm.total_amount).toLocaleString('en-IN') : 'N/A'}</p>
                                </>
                            )}

                            {selectedForm.services && (
                                <div className="md:col-span-2">
                                    <p className="font-semibold mb-2">Services:</p>
                                    <ul className="flex flex-wrap gap-2">
                                        {parseServices(selectedForm.services).map((service, i) => (
                                            <li key={i} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                                                {service}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="md:col-span-2 mt-2">
                                <p className="font-semibold">Description:</p>
                                <p className="p-2 bg-gray-50 rounded-md border">{selectedForm.description || 'No description provided.'}</p>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default Profile;